<template>
  <div class="variable-input">
    <component v-if="field.form.component"
               :is="formcomponents[field.form.component]"
               :field="field"
               :object="object"
               :required="required"
               v-model="object[field.id]"
               :selectOptions="selectOptions"
               :selectModels="selectModels">
    </component>
    <InputGroup v-else-if="['text','number','color','password'].includes(type)"
                :label="$t(field.title)"
                :type="type"
                :readonly="readonly"
                :required="required"
                :validation-id="field.validation_id || field.id"
                :render-result="renderResult"
                @change="$emit('change')"
                @enter="$emit('enter')"
                v-model="object[field.id]"/>
    <CheckboxGroup v-else-if="type === 'checkbox'"
                   :label="$t(field.title)"
                   :fid="`checkbox-${field.id}`"
                   :required="required"
                   v-model="object[field.id]"/>
    <FileGroup v-else-if="type === 'file'"
               :label="$t(field.title)"
               :fid="`file-${field.id}`"
               :deletefile="!!field.form.deletefile"
               :sizelimit="field.form.sizelimit ? field.form.sizelimit : 10"
               :multiple="field.form.multiple && !(field.form.deletefile && edit)"
               :webkitdirectory="field.form.allowDirectories || false"
               :filetype="field.form.filetype"
               :required="required"
               :readonly="readonly"
               v-model="object[field.id]"/>
    <DateGroup v-else-if="type === 'date'"
               :label="$t(field.title)"
               :readonly="readonly"
               :required="required"
               v-model="object[field.id]"/>
    <DateTimeGroup v-else-if="type === 'datetime'"
                   :showlabel="false"
                   :label="$t(field.title)"
                   :required="required"
                   v-model="object[field.id]"/>
    <TimeGroup v-else-if="type === 'time'"
               :label="$t(field.title)"
               :required="required"
               v-model="object[field.id]"/>
    <AutosizeTextArea v-else-if="type === 'textarea'"
                      :label="$t(field.title)"
                      :max-length="field.form.max_length"
                      :rich-text="field.form.richText"
                      :required="required"
                      v-model="object[field.id]"/>
    <RichTextArea v-else-if="type === 'richtext'"
                      :label="$t(field.title)"
                      :max-length="field.form.max_length"
                      :required="required"
                      v-model="object[field.id]"/>
    <SelectGroup v-else-if="type === 'select' && (field.form.select_type === 'list' || selectOptions.length > 0) && !field.form.multiple"
                 :options="field.form.select_type === 'list' ? Object.values(field.form.options) : selectOptions"
                 variant="white"
                 :title="field.form.select_data && field.form.select_data.title ? field.form.select_data.title : null"
                 :label="$t(field.title)"
                 :required="required"
                 :readonly="readonly"
                 :translate="field.form.translate !== false"
                 :nullOption="!field.form.hideNullOption"
                 :nullOptionTitle="field.form.nullOptionTitle ? $t(field.form.nullOptionTitle) : '-'"
                 :showlabel="true"
                 v-model="object[field.id]"/>
    <ServiceMultiSelect v-else-if="type === 'select' && (field.form.select_type === 'list' || selectOptions.length > 0) && field.form.multiple"
                        :label="$t(field.title)"
                        :dblclick="false"
                        :multiple="true"
                        :prepopulated-options="field.form.select_type === 'list' ? Object.values(field.form.options) : selectOptions"
                        :title="field.form.select_data.title ? field.form.select_data.title : 'title'"
                        :model="field.form.model"
                        :dbsearch="false"
                        v-model="object[field.id]"/>
    <CurrencyJsonGroup v-else-if="['currency_json'].includes(type)"
                       :label="$t(field.title)"
                       :readonly="readonly"
                       :required="required"
                       @enter="$emit('enter')"
                       v-model="object[field.id]"/>
    <ServiceMultiSelect v-else-if="['autocomplete'].includes(type)"
                        :label="$t(field.title)"
                        :dblclick="false"
                        :multiple="false"
                        :required="required"
                        :title="field.form.autocomplete_title ? field.form.autocomplete_title : 'name'"
                        :model="field.form.model"
                        :dbsearch="true"
                        v-model="object[field.id]"/>
    <div v-else-if="type === 'separator'">
      <div class="separator-title" v-if="field.form.title">{{ $t(field.form.title) }}</div>
      <div class="separator"></div>
    </div>
    <div v-if="field.form.note" class="note-div text-muted small">{{ field.form.noteDoNotTransate ? field.form.note : $t(field.form.note) }}</div>
  </div>
</template>

<script setup>
import InputGroup from "@/components/form/InputGroup"
import SelectGroup from "@/components/form/SelectGroup"
import DateGroup from "@/components/form/DateGroup"
import CheckboxGroup from "@/components/form/CheckboxGroup"
import AutosizeTextArea from "@/components/form/AutosizeTextArea"
import FileGroup from "@/components/form/FileGroup"
import TimeGroup from "@/components/form/TimeGroup"
import CurrencyJsonGroup from "@/components/form/CurrencyJsonGroup"
import ServiceMultiSelect from "@/components/form/ServiceMultiSelect"
import DateTimeGroup from "@/components/form/DateTimeGroup"
import { computed, defineAsyncComponent } from "vue"
import { useStore } from "vuex"
import { asyncComponentsFromContext } from "@/services/components.helper"

// async: it brings the jodit editor, only a few forms have a richtext field
const RichTextArea = defineAsyncComponent(() => import("@/components/form/RichTextArea.vue"))

// One field of a datatable form (FormModal), rendered by its field.form.type, or by its own form component
const props = defineProps({
  field: {
    type: Object,
    required: true
  },
  object: {
    type: Object,
  },
  selectModels: {
    type: Object
  },
  edit: {
    type: Boolean,
    default: false
  }
})

defineEmits(['enter', 'change'])

const store = useStore()

const type = computed(() => props.field.form.type)

const selectOptions = computed(() => {
  if(!props.selectModels || !props.selectModels[props.field.id]) {
    return []
  }
  return props.selectModels[props.field.id]
})

const required = computed(() => props.field.form.required ? props.field.form.required : false)

// readonly can be a function of the store
const readonly = computed(() => {
  if((props.field.form.disabled_on_edit && props.edit)) return true
  if(props.field.form.readonly && typeof props.field.form.readonly === 'function') {
    return props.field.form.readonly(store)
  }
  return props.field.form.readonly
})

const renderResult = computed(() => {
  if(!props.field.form.capitalize_first_letter) return undefined
  return (val) => val ? val.charAt(0).toUpperCase() + val.slice(1) : val
})

// the form components (field.form.component) by file name - collected on first use, not on import: some of them
// import VariableInput themselves
const formcomponents = computed(() => {
  return asyncComponentsFromContext(require.context(`@/components/parts/datatable/form/`, true, /\.vue$/i, 'lazy'))
})
</script>

<style scoped lang="scss">
.separator {
  height: 1px;
  background: #E0E7EE;
  width: 100%;
  margin: 15px 0;
}
.separator-title {
  font-weight: 700;
  margin-top: 20px;
  color: #87939E;
}
.note-div {
  position: relative;
  top: -12px;
}
</style>
